<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';

const emit = defineEmits(['submit']);

const dialogRef = ref(null);
const conversationContext = ref(null);

const form = reactive({
  result: '',
  productName: '',
  productAmount: '',
  noSaleReason: '',
  notes: '',
});

const resultOptions = [
  { value: 'sale', label: 'Venta' },
  { value: 'no_sale', label: 'No venta' },
];

const noSaleReasonOptions = [
  { value: 'no_response', label: 'El cliente dejó de contestar' },
  { value: 'too_expensive', label: 'Le pareció caro' },
  { value: 'information_only', label: 'Solo quería información' },
  { value: 'unavailable', label: 'No había disponibilidad' },
  { value: 'outside_service_area', label: 'Fuera de zona de servicio' },
  { value: 'bought_elsewhere', label: 'Compró en otro sitio' },
  { value: 'unavailable_date', label: 'Fecha u horario no disponible' },
  { value: 'other', label: 'Otro' },
];

const numericAmount = computed(() => Number(form.productAmount || 0));
const commissionAmount = computed(() =>
  Number.isFinite(numericAmount.value)
    ? Math.round(numericAmount.value * 25) / 100
    : 0
);

const formattedCommission = computed(() =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(commissionAmount.value)
);

const isFormComplete = computed(() => {
  if (form.result === 'sale') {
    return (
      form.productName.trim().length > 0 &&
      numericAmount.value > 0
    );
  }

  if (form.result === 'no_sale') {
    return form.noSaleReason.length > 0;
  }

  return false;
});

const resetForm = () => {
  form.result = '';
  form.productName = '';
  form.productAmount = '';
  form.noSaleReason = '';
  form.notes = '';
};

const open = context => {
  resetForm();
  conversationContext.value = context;
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
  conversationContext.value = null;
};

const handleConfirm = () => {
  if (!isFormComplete.value) return;

  emit('submit', {
    closure: {
      result: form.result,
      product_name: form.productName.trim(),
      product_amount: form.productAmount,
      no_sale_reason: form.noSaleReason,
      notes: form.notes.trim(),
    },
    context: conversationContext.value,
  });

  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    title="Cerrar y clasificar conversación"
    description="Registra el resultado antes de cerrar la conversación."
    confirm-button-label="Guardar y cerrar"
    cancel-button-label="Cancelar"
    :disable-confirm-button="!isFormComplete"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Resultado
        </label>
        <ComboBox
          v-model="form.result"
          :options="resultOptions"
          placeholder="Selecciona Venta o No venta"
          class="w-full"
        />
      </div>

      <template v-if="form.result === 'sale'">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Producto vendido
          </label>
          <Input
            v-model="form.productName"
            type="text"
            size="md"
            placeholder="Ejemplo: Corona premium"
          />
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Precio de los productos (MXN)
          </label>
          <Input
            v-model="form.productAmount"
            type="number"
            min="0.01"
            step="0.01"
            size="md"
            placeholder="0.00"
          />
        </div>

        <div class="rounded-lg bg-n-alpha-2 p-4">
          <div class="text-sm text-n-slate-11">Comisión del proveedor (25 %)</div>
          <div class="mt-1 text-lg font-semibold text-n-slate-12">
            {{ formattedCommission }}
          </div>
        </div>
      </template>

      <div
        v-else-if="form.result === 'no_sale'"
        class="flex flex-col gap-2"
      >
        <label class="text-sm font-medium text-n-slate-12">
          Motivo de No venta
        </label>
        <ComboBox
          v-model="form.noSaleReason"
          :options="noSaleReasonOptions"
          placeholder="Selecciona un motivo"
          class="w-full"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Observaciones opcionales
        </label>
        <TextArea
          v-model="form.notes"
          class="w-full"
          placeholder="Añade cualquier detalle que quieras conservar"
        />
      </div>
    </div>
  </Dialog>
</template>
